import { useQuery } from '@tanstack/react-query';
import { Card } from '@/shared/components/ui/Card';
import { reportsService } from '../services/reportsService';
import type { LocationSummaryFilters, AgingExceptionsFilters, UsageComplianceFilters } from '../services/reportsService';

interface ReportsSummaryDashboardProps {
  filters: LocationSummaryFilters;
  isMultiLocation: boolean;
}

interface StatCardProps {
  label: string;
  value: number | string;
  subLabel?: string;
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'gray';
  loading?: boolean;
}

function StatCard({ label, value, subLabel, color = 'blue', loading }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  };

  const valueColorClasses = {
    blue: 'text-blue-900',
    green: 'text-green-900',
    yellow: 'text-yellow-900',
    red: 'text-red-900',
    gray: 'text-gray-900',
  };

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color]}`}>
      <p className="text-sm font-medium">{label}</p>
      {loading ? (
        <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1" />
      ) : (
        <p className={`text-2xl font-bold mt-1 ${valueColorClasses[color]}`}>{value}</p>
      )}
      {subLabel && <p className="text-xs mt-1 opacity-75">{subLabel}</p>}
    </div>
  );
}

export function ReportsSummaryDashboard({ filters, isMultiLocation }: ReportsSummaryDashboardProps) {
  // Fetch location summary data
  const { data: locationSummary, isLoading: summaryLoading } = useQuery({
    queryKey: ['reports', 'locationSummary', filters],
    queryFn: () => reportsService.getLocationSummary(filters),
  });

  // Fetch aging exceptions (PENDING and ORDERED items that need attention)
  const agingFilters: AgingExceptionsFilters = {
    location_ids: filters.location_ids,
    min_days: 7, // Items older than 7 days
  };
  const { data: agingExceptions, isLoading: agingLoading } = useQuery({
    queryKey: ['reports', 'agingExceptions', agingFilters],
    queryFn: () => reportsService.getAgingExceptions(agingFilters),
  });

  // Fetch usage compliance
  const usageFilters: UsageComplianceFilters = {
    date_from: filters.date_from,
    date_to: filters.date_to,
    location_ids: filters.location_ids,
  };
  const { data: usageCompliance, isLoading: usageLoading } = useQuery({
    queryKey: ['reports', 'usageCompliance', usageFilters],
    queryFn: () => reportsService.getUsageCompliance(usageFilters),
  });

  // Aggregate totals
  const totals = locationSummary?.reduce(
    (acc, loc) => ({
      framesAdded: acc.framesAdded + loc.frames_added,
      framesOrdered: acc.framesOrdered + loc.frames_ordered,
      framesReceived: acc.framesReceived + loc.frames_received,
    }),
    { framesAdded: 0, framesOrdered: 0, framesReceived: 0 }
  ) || { framesAdded: 0, framesOrdered: 0, framesReceived: 0 };

  const exceptionsCount = agingExceptions?.length || 0;

  // Calculate average usage percentage
  const avgUsage = usageCompliance && usageCompliance.length > 0
    ? Math.round(
        usageCompliance.reduce((sum, loc) => sum + loc.usage_percentage, 0) / usageCompliance.length
      )
    : 0;

  // Determine exception severity color
  const exceptionColor = exceptionsCount === 0 
    ? 'green' 
    : exceptionsCount < 10 
    ? 'yellow' 
    : 'red';

  // Determine usage color
  const usageColor = avgUsage >= 80 ? 'green' : avgUsage >= 50 ? 'yellow' : 'red';

  const isLoading = summaryLoading || agingLoading || usageLoading;

  return (
    <Card className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Quick Summary</h2>
        {isMultiLocation && (
          <span className="text-sm text-gray-500">
            {locationSummary?.length || 0} location(s)
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard
          label="Frames Added"
          value={totals.framesAdded}
          subLabel="In date range"
          color="blue"
          loading={isLoading}
        />
        <StatCard
          label="Frames Ordered"
          value={totals.framesOrdered}
          subLabel="Sent to manufacturers"
          color="blue"
          loading={isLoading}
        />
        <StatCard
          label="Frames Received"
          value={totals.framesReceived}
          subLabel="Completed"
          color="green"
          loading={isLoading}
        />
        <StatCard
          label="Exceptions"
          value={exceptionsCount}
          subLabel="Need attention (7+ days)"
          color={exceptionColor}
          loading={isLoading}
        />
        <StatCard
          label="Usage Rate"
          value={`${avgUsage}%`}
          subLabel="Avg. compliance"
          color={usageColor}
          loading={isLoading}
        />
      </div>

      {/* Action items */}
      {!isLoading && exceptionsCount > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            <span className="font-medium text-red-600">{exceptionsCount} items</span> need attention in the Aging & Exceptions report.
          </p>
        </div>
      )}
    </Card>
  );
}

